// Create a new file: src/components/admin/UserCreditsManagement.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Users, Plus, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';;

const UserCreditsManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState(5);
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const { getIdToken } = useAuth();
  
  // Fetch all users with their credit information
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // This would be a new endpoint to get all users with their credit info
      // For now, we'll use the existing endpoint that returns users in analysis data
      const response = await fetch(`${API_BASE_URL}/admin/analyses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const analyses = await response.json();
      
      // Extract unique users from analyses
      const uniqueUsers = {};
      analyses.forEach(analysis => {
        if (analysis.user_id && analysis.user_email) {
          if (!uniqueUsers[analysis.user_id]) {
            uniqueUsers[analysis.user_id] = {
              id: analysis.user_id,
              email: analysis.user_email,
              displayName: analysis.user_displayName || 'Unknown',
              country: analysis.user_country || 'Unknown'
            };
          }
        }
      });
      
      setUsers(Object.values(uniqueUsers));
      setError(null);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Function to add credits to a user
  const handleAddCredits = async () => {
    if (!selectedUser) return;
    
    try {
      setProcessing(true);
      setSuccessMessage(null);
      setError(null);
      
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/add-credits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          credits: parseInt(creditsToAdd)
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to add credits');
      }
      
      setSuccessMessage(`Successfully added ${creditsToAdd} credits to ${selectedUser.email}`);
      
      // Reset form
      setCreditsToAdd(5);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('Error adding credits:', error);
      setError('Failed to add credits: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
        <Users className="w-5 h-5 mr-2" />
        Manage User Credits
      </h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-start">
          <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{successMessage}</div>
        </div>
      )}
      
      {/* Search Users */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search users by email, name or country..."
            className="w-full p-2 pl-10 border rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
      </div>
      
      {/* User List */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-700 mb-2">Select User</h3>
        <div className="max-h-60 overflow-y-auto border rounded-lg">
          {loading ? (
            <div className="flex justify-center items-center p-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading users...</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No users found
            </div>
          ) : (
            filteredUsers.map(user => (
              <div
                key={user.id}
                onClick={() => setSelectedUser(user)}
                className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                  selectedUser?.id === user.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="font-medium">{user.displayName}</div>
                <div className="text-sm text-gray-600">{user.email}</div>
                <div className="text-xs text-gray-500">Country: {user.country}</div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Add Credits Form */}
      {selectedUser && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h3 className="text-md font-medium text-gray-700 mb-3">
            Add Credits to {selectedUser.displayName}
          </h3>
          
          <div className="flex items-end space-x-4 mb-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Credits to Add
              </label>
              <input
                type="number"
                min="1"
                value={creditsToAdd}
                onChange={(e) => setCreditsToAdd(Math.max(1, parseInt(e.target.value) || 0))}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <button
              onClick={handleAddCredits}
              disabled={processing}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center disabled:bg-gray-400"
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Credits
                </>
              )}
            </button>
          </div>
          
          <div className="text-sm text-gray-600">
            This will add {creditsToAdd} credits to the user's account. This operation cannot be undone.
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCreditsManagement;